import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { easeLux } from "@/lib/motion";

// Modal de galería fotográfica: cuadrícula tipo mosaico + lightbox con navegación
export const GalleryModal = ({ gallery, onClose }) => {
  const [index, setIndex] = useState(null); // null => cuadrícula; número => lightbox
  const images = gallery?.images || [];

  const close = useCallback(() => {
    setIndex(null);
    onClose();
  }, [onClose]);

  const prev = useCallback(
    (e) => {
      e && e.stopPropagation();
      setIndex((i) => (i === null ? i : (i - 1 + images.length) % images.length));
    },
    [images.length]
  );
  const next = useCallback(
    (e) => {
      e && e.stopPropagation();
      setIndex((i) => (i === null ? i : (i + 1) % images.length));
    },
    [images.length]
  );

  useEffect(() => {
    if (!gallery) return;
    const onKey = (e) => {
      if (e.key === "Escape") index === null ? close() : setIndex(null);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [gallery, index, close, next, prev]);

  // Reinicia el índice al abrir otra galería
  useEffect(() => {
    setIndex(null);
  }, [gallery?.id]);

  return (
    <AnimatePresence>
      {gallery && (
        <motion.div
          data-testid="gallery-modal"
          className="fixed inset-0 z-[60] flex flex-col bg-ivory"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: easeLux }}
          data-lenis-prevent
        >
          {/* Cabecera */}
          <div className="flex items-center justify-between border-b border-rosegold/20 px-6 py-5 md:px-12">
            <div>
              <p className="text-[0.6rem] uppercase tracking-[0.3em] text-rosegold">
                Fotografía · {images.length} fotos
              </p>
              <h3 className="mt-1 font-serif text-2xl md:text-3xl text-plum">{gallery.title}</h3>
            </div>
            <button
              onClick={close}
              aria-label="Cerrar"
              data-testid="gallery-modal-close"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-plum/30 text-plum transition-[background-color,color] duration-500 hover:bg-plum hover:text-ivory"
            >
              <X strokeWidth={1.5} />
            </button>
          </div>

          {/* Cuadrícula tipo mosaico */}
          <div className="flex-1 overflow-y-auto px-6 py-8 md:px-12" data-lenis-prevent>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
              {images.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setIndex(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className="group mb-4 md:mb-6 block w-full overflow-hidden break-inside-avoid"
                >
                  <img
                    src={src}
                    alt={`${gallery.title} ${i + 1}`}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Lightbox */}
          <AnimatePresence>
            {index !== null && (
              <motion.div
                className="fixed inset-0 z-[70] flex items-center justify-center bg-plum/95 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: easeLux }}
                onClick={() => setIndex(null)}
                data-testid="gallery-lightbox"
              >
                <button
                  onClick={() => setIndex(null)}
                  aria-label="Cerrar"
                  className="absolute right-5 top-5 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-ivory/10 text-ivory transition-colors duration-500 hover:bg-ivory hover:text-plum"
                >
                  <X strokeWidth={1.5} />
                </button>
                <button
                  onClick={prev}
                  aria-label="Anterior"
                  data-testid="gallery-prev"
                  className="absolute left-3 md:left-8 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-ivory/10 text-ivory transition-colors duration-500 hover:bg-rosegold"
                >
                  <ChevronLeft strokeWidth={1.5} />
                </button>
                <button
                  onClick={next}
                  aria-label="Siguiente"
                  data-testid="gallery-next"
                  className="absolute right-3 md:right-20 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-ivory/10 text-ivory transition-colors duration-500 hover:bg-rosegold"
                >
                  <ChevronRight strokeWidth={1.5} />
                </button>

                <motion.img
                  key={images[index]}
                  src={images[index]}
                  alt={`${gallery.title} ampliada`}
                  onClick={(e) => e.stopPropagation()}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, ease: easeLux }}
                  className="max-h-[85vh] max-w-[90vw] object-contain"
                />
                <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.2em] text-ivory/60">
                  {index + 1} / {images.length}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GalleryModal;
